import { Component, ChangeDetectionStrategy, Input, Output, ViewChild, ElementRef, OnDestroy } from '@angular/core'
import { ReplaySubject, Subject } from 'rxjs'
import { map, takeUntil, withLatestFrom, filter } from 'rxjs/operators'
import { VideoEndedEvent } from '../../../../collections/collections.models'
import { trackById } from '../../../../shared/utility-functions/track-by-id.utility'
import { getAnalytics } from '../../../collections/functions/analytics'
import { CollectionChildNodesRequestParams } from 'src/io/models/http/collection-child-nodes-request-params'
import { Node } from 'src/io/models/node'
import { VideoPlayerCardPartial } from '@flocasts/experience-service-types'

export interface NodeListComponentProps {
  nodes: ReadonlyArray<Node>
  nodesRequestParams?: CollectionChildNodesRequestParams
  selectedNode?: Node
}

@Component({
  selector: 'flo-video-list',
  templateUrl: `./video-list.component.html`,
  styles: [
    `
      .empty-text {
        margin: 30px 0;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoListComponent implements OnDestroy {
  private ngOnDestroy$ = new ReplaySubject<void>(1)
  private readonly videoPlayer$ = new ReplaySubject<HTMLElement>(1)
  public readonly emitThumbnailClick$ = new ReplaySubject<boolean>(1)

  @ViewChild('videoPlayer', { read: ElementRef })
  set videoPlayerContainer(videoPlayerContainer: ElementRef<HTMLElement>) {
    if (videoPlayerContainer?.nativeElement) {
      this.videoPlayer$.next(videoPlayerContainer.nativeElement)
    }
  }

  private readonly props$ = new ReplaySubject<NodeListComponentProps>(1)
  @Input()
  set props(props: NodeListComponentProps) {
    if (!!props) {
      this.props$.next(props)
    }
  }

  /**
   * Support selected video player card from BFF
   */
  @Input() selectedItem: VideoPlayerCardPartial | undefined

  @Output()
  public readonly action$ = new Subject<VideoEndedEvent>()

  public readonly selectedNode$ = this.props$.pipe(
    filter(props => !!props.selectedNode),
    map(props => ({
      ...props.selectedNode,
      collectionId: props.nodesRequestParams && props.nodesRequestParams.collectionId
    }))
  )

  public readonly listItemProps$ = this.props$.pipe(
    map(props => props.nodes),
    map(nodes =>
      nodes.map((node, i, arr) => {
        return {
          node,
          type: node.type,
          analyticsProps: getAnalytics(node as Node, i, arr.length)
        }
      })
    )
  )

  public readonly emitVideoEndedEvent = () => this.action$.next(new VideoEndedEvent())

  public scrollToVideoPlayer$ = this.emitThumbnailClick$
    .pipe(withLatestFrom(this.videoPlayer$), takeUntil(this.ngOnDestroy$))
    .subscribe(([thumbnailClick, player]) => {
      player.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center'
      })
    })

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  public trackById = trackById
}
